import React, { useEffect, useState } from "react";
import styled from "styled-components";
import sampleTracings from "../data/sample_tracings.json";
import sampleOutput from "../data/sample_output.json";
import sampleAttributes from "../data/attributes.json";
import { EcgViz } from "./EcgViz";
import { HeartViz } from "./HeartViz";
import { Legend } from "./Legend";
import { PredictionTable } from "./PredictionTable";
import { SearchEcg } from "./SearchEcg";
import { Stats } from "./Stats";
import { breakpoints } from "../styles";
import { isMobile } from "../utils";

interface AttributeArray {
    [index: string]: { sex: string; age: string };
}

interface Output {
    [index: string]: number[];
}

interface Tracings {
    [index: string]: number[][];
}

const labels = [
    "1st degree AV block (1dAVb)",
    "Right bundle branch block (RBBB)",
    "Left bundle branch block (LBBB)",
    "Sinus bradycardia (SB)",
    "Atrial fibrillation (AF)",
    "Sinus tachycardia (ST)",
    "Normal",
];

export const Dashboard = () => {
    const attributes: AttributeArray = sampleAttributes;
    const output: Output = JSON.parse(sampleOutput);
    const tracings: Tracings = JSON.parse(sampleTracings.toString());
    const sampleSize = 100;

    const [patient, setPatient] = useState<string>("0");
    const [prediction, setPrediction] = useState<number[]>(output["0"]);
    const [expand, setExpand] = useState(false);
    const [selectionFilter, setSelectionFilter] = useState<number[]>(
        Array.from(Array(sampleSize).keys())
    );

    const handleSelectPatient = (patient: string) => {
        setPatient(patient);
        setPrediction(output[patient]);
        setExpand(false);
    };

    return (
        <Container>
            <Toolbar>
                <div className="title is-4 has-text-white">Select Patient</div>
                <div className="select is-small">
                    <select
                        className="has-background-black-ter has-text-white"
                        onChange={(event) =>
                            handleSelectPatient(event.target.value)
                        }
                    >
                        {selectionFilter.map((patientNum) => {
                            return (
                                <option value={patientNum} key={patientNum}>
                                    Patient {patientNum}
                                </option>
                            );
                        })}
                    </select>
                </div>
                <div>
                    {" "}
                    Age: {attributes[patient].age}, Sex:{" "}
                    {attributes[patient].sex}
                </div>
                <br />
                <div>Total Samples Available: {selectionFilter.length}</div>
                <br />
                <SearchEcg
                    sampleSize={sampleSize}
                    output={output}
                    setSelectionFilter={setSelectionFilter}
                    handleSelectPatient={handleSelectPatient}
                />
            </Toolbar>
            <Main>
                <VizContainer>
                    {!isMobile && (
                        <EcgViz
                            patientEcg={tracings[patient]}
                            prediction={prediction}
                        />
                    )}
                    <HeartViz prediction={prediction} />
                    <LegendContainer>
                        <Legend />
                    </LegendContainer>
                </VizContainer>
                <StatsContainer>
                    <Stats prediction={prediction} />
                    {!expand && (
                        <ExpandButton onClick={() => setExpand(true)}>
                            Details
                        </ExpandButton>
                    )}
                    {expand && (
                        <PredictionContainer>
                            <PredictionTable prediction={prediction} />
                        </PredictionContainer>
                    )}
                </StatsContainer>
            </Main>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: row;
    gap: 2%;
    color: white;
    padding: 2%;
    min-height: 100vh;

    @media ${breakpoints.md} {
        flex-direction: column;
        gap: 1vh;
    }
`;

const Header = styled.div`
    border: 2px solid white;
    padding: 3%;
    flex: 1;
`;

const HeaderTitle = styled.span`
    font-size: 2em;
`;

const Main = styled.div`
    flex: 10;
    display: flex;
    gap: 2%;

    @media ${breakpoints.md} {
        flex-direction: column;
        /* gap: 1vh; */
    }
`;

const VizContainer = styled.div`
    border: 2px solid white;
    padding: 3%;
    display: flex;
    justify-content: center;
    flex: 4;
    position: relative;
    max-height: 75vh;

    @media ${breakpoints.md} {
        flex-direction: column;
    }
`;

const LegendContainer = styled.div`
    position: absolute;
    bottom: 2vh;
    left: 2vw;
    width: 100%;
`;

const Toolbar = styled.div`
    border: 2px solid white;
    padding: 2%;
    flex: 1;
`;

const StatsContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2%;

    @media ${breakpoints.md} {
        /* gap: 1vh; */
    }
`;

const PredictionContainer = styled.div`
    border: 2px solid white;
    /* padding: 2%; */
    flex: 1;
`;

const ExpandButton = styled.button`
    background: none;
    border: 2px solid white;
    color: white;
    padding: 3%;
    &:hover {
        background: grey;
        cursor: pointer;
    }
`;
